import React, { useState, useMemo } from 'react';
import {
  Box,
  Text,
  HStack,
  VStack,
  Code,
  IconButton,
  createListCollection,
  Portal,
  Input,
  InputGroup,
} from '@chakra-ui/react';
import { Select } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { toaster } from './ui/toaster';
import { environment } from '../config/environment';
import { FaCopy, FaSearch } from 'react-icons/fa';
import { useColorModeValue } from './ui/color-mode';
import { ReadmeViewer } from './ReadmeViewer';

interface ModuleVersion {
  version: string;
  providers: string[];
}

interface Module {
  source: string;
  versions: ModuleVersion[];
}

// Function to sort versions by semantic version (latest first)
const sortVersionsBySemanticVersion = (versions: ModuleVersion[]): ModuleVersion[] => {
  return [...versions].sort((a, b) => {
    const parseVersion = (version: string) => {
      const parts = version.replace(/^v/, '').split('.').map(Number);
      return {
        major: parts[0] || 0,
        minor: parts[1] || 0,
        patch: parts[2] || 0,
      };
    };

    const aVersion = parseVersion(a.version);
    const bVersion = parseVersion(b.version);

    // Sort in descending order (latest first)
    if (aVersion.major !== bVersion.major) return bVersion.major - aVersion.major;
    if (aVersion.minor !== bVersion.minor) return bVersion.minor - aVersion.minor;
    return bVersion.patch - aVersion.patch;
  });
};

export const ModuleList: React.FC = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.600');
  const versionBg = useColorModeValue('gray.50', 'gray.700');
  const codeBg = useColorModeValue('gray.100', 'gray.900');
  const textColor = useColorModeValue('gray.900', 'gray.100');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
  const searchBg = useColorModeValue('white', 'gray.700');
  const searchBorder = useColorModeValue('gray.300', 'gray.600');

  // State for search and global version selection
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [globalSelectedVersion, setGlobalSelectedVersion] = useState<string>('');

  const { data: modules, isLoading, error } = useQuery({
    queryKey: ['modules'],
    queryFn: async () => {
      try {
        const response = await axios.get(`${environment.apiBaseUrl}/v1/modules`);
        console.log('API Response:', JSON.stringify(response.data, null, 2));
        return response.data.modules || [];
      } catch (err) {
        console.error('API Error:', err);
        // Return empty array instead of throwing to prevent crash
        return [];
      }
    },
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Get all unique versions across all modules for global version selection
  const allVersions = useMemo(() => {
    if (!modules || modules.length === 0) return [];
    
    const versionSet = new Set<string>();
    modules.forEach((module: Module) => {
      module.versions.forEach((version) => {
        versionSet.add(version.version);
      });
    });
    
    const uniqueVersions = Array.from(versionSet).map(version => ({ version, providers: [] }));
    return sortVersionsBySemanticVersion(uniqueVersions);
  }, [modules]);

  // Set default global version to the latest available version
  useMemo(() => {
    if (allVersions.length > 0 && !globalSelectedVersion) {
      setGlobalSelectedVersion(allVersions[0].version);
    }
  }, [allVersions, globalSelectedVersion]);

  // Filter modules based on search term
  const filteredModules = useMemo(() => {
    if (!modules) return [];
    
    if (!searchTerm.trim()) return modules;
    
    return modules.filter((module: Module) =>
      module.source.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [modules, searchTerm]);

  // Create collection for the global version selector
  const versionCollection = useMemo(() => {
    return createListCollection({
      items: allVersions.map(version => ({
        label: version.version === allVersions[0]?.version ? `${version.version} (latest)` : version.version,
        value: version.version,
      }))
    });
  }, [allVersions]);

  const copyToClipboard = async (text: string, description: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toaster.create({
        title: 'Copied to clipboard',
        description,
        type: 'success',
        duration: 2000,
      });
    } catch (error) {
      toaster.create({
        title: 'Failed to copy',
        description: 'Could not copy to clipboard',
        type: 'error',
        duration: 3000,
      });
    }
  };

  const generateSourceBlock = (source: string, version: string) => {
    // Extract module name from source (e.g., "acertus/rest-api-gw/aws" -> "rest_api_gw")
    const parts = source.split('/');
    const moduleName = parts.length > 1 ? parts[1].replace(/-/g, '_') : 'module';
    
    return `module "my_${moduleName}" {
  source  = "${environment.registryDomain}/${source}"
  version = "${version}"
  
  # Add your module configuration here
}`;
  };

  const handleGlobalVersionChange = (details: { value: string[] }) => {
    if (details.value.length > 0) {
      setGlobalSelectedVersion(details.value[0]);
    }
  };

  if (isLoading) {
    return (
      <Box p={4} textAlign="center">
        <Text color={textColor}>Loading modules...</Text>
      </Box>
    );
  }

  if (error) {
    console.error('Query Error:', error);
    return (
      <Box p={4} textAlign="center">
        <Text color="red.500">Error loading modules. Please check the API connection.</Text>
        <Text fontSize="sm" color={mutedTextColor} mt={2}>
          API URL: {environment.apiBaseUrl}/v1/modules
        </Text>
      </Box>
    );
  }

  if (!modules || modules.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Text color={textColor}>No modules available. Upload your first module to get started.</Text>
        <Text fontSize="sm" color={mutedTextColor} mt={2}>
          API URL: {environment.apiBaseUrl}/v1/modules
        </Text>
      </Box>
    );
  }

  return (
    <VStack gap={6} align="stretch">
      {/* Search and Global Version Controls */}
      <VStack gap={4} align="stretch">
        {/* Search Bar */}
        <Box>
          <Text fontSize="sm" color={mutedTextColor} mb={2}>
            Search Modules
          </Text>
          <InputGroup startElement={<FaSearch />}>
            <Input
              placeholder="Search modules by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              bg={searchBg}
              borderColor={searchBorder}
              color={textColor}
              _placeholder={{ 
                color: mutedTextColor 
              }}
              _focus={{
                borderColor: 'blue.500',
                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
              }}
            />
          </InputGroup>
        </Box>

        {/* Global Version Selector */}
        {allVersions.length > 0 && (
          <Box>
            <Text fontSize="sm" color={mutedTextColor} mb={2}>
              Select Version (applies to all modules)
            </Text>
            <HStack>
              <Select.Root
                size="sm"
                value={[globalSelectedVersion]}
                onValueChange={handleGlobalVersionChange}
                collection={versionCollection}
                width="auto"
                minW="180px"
              >
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Portal>
                  <Select.Positioner>
                    <Select.Content 
                      bg={cardBg} 
                      borderColor={cardBorder}
                      boxShadow="lg"
                    >
                      {versionCollection.items.map((item) => (
                        <Select.Item 
                          key={item.value} 
                          item={item}
                          _hover={{ bg: versionBg }}
                          color={textColor}
                        >
                          <Select.ItemText>{item.label}</Select.ItemText>
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Portal>
              </Select.Root>
              <Text fontSize="sm" color={mutedTextColor}>
                {allVersions.length} version{allVersions.length > 1 ? 's' : ''} available
              </Text>
            </HStack>
          </Box>
        )}

        {/* Results Summary */}
        <Text fontSize="sm" color={mutedTextColor}>
          {searchTerm.trim() ? (
            <>Showing {filteredModules.length} of {modules.length} modules</>
          ) : (
            <>{modules.length} module{modules.length > 1 ? 's' : ''} available</>
          )}
        </Text>
      </VStack>

      {/* Module List */}
      {filteredModules.length === 0 ? (
        <Box p={8} textAlign="center">
          <Text color={textColor} fontSize="lg" mb={2}>
            No modules found
          </Text>
          <Text color={mutedTextColor}>
            {searchTerm.trim() 
              ? `No modules match "${searchTerm}". Try a different search term.`
              : 'No modules available.'}
          </Text>
        </Box>
      ) : (
        <VStack gap={4} align="stretch">
          {filteredModules.map((module: Module) => {
            const sortedVersions = sortVersionsBySemanticVersion(module.versions);
            const moduleHasSelectedVersion = sortedVersions.some(v => v.version === globalSelectedVersion);
            const versionToShow = moduleHasSelectedVersion 
              ? globalSelectedVersion 
              : sortedVersions[0]?.version || '';

            return (
              <Box 
                key={module.source} 
                p={4} 
                borderWidth="1px" 
                borderColor={cardBorder}
                borderRadius="md"
                bg={cardBg}
              >
                <VStack align="start" gap={3}>
                  <HStack justify="space-between" w="full">
                    <Text fontWeight="bold" fontSize="lg" color={textColor}>
                      {module.source}
                    </Text>
                    {!moduleHasSelectedVersion && (
                      <Text fontSize="xs" color="orange.500" fontWeight="medium">
                        Using latest: {versionToShow}
                      </Text>
                    )}
                  </HStack>
                  
                  <Text fontSize="sm" color={mutedTextColor}>
                    {sortedVersions.length} version{sortedVersions.length > 1 ? 's' : ''} available: {' '}
                    {sortedVersions.slice(0, 3).map(v => v.version).join(', ')}
                    {sortedVersions.length > 3 && ` and ${sortedVersions.length - 3} more`}
                  </Text>
                  
                  <Box 
                    w="full" 
                    p={3} 
                    bg={versionBg} 
                    borderRadius="md"
                  >
                    <HStack justify="space-between" align="start" mb={2}>
                      <Text fontWeight="semibold" color={textColor}>
                        Version {versionToShow}
                        {versionToShow === sortedVersions[0]?.version && (
                          <Text as="span" fontSize="xs" color="green.500" ml={2}>
                            LATEST
                          </Text>
                        )}
                      </Text>
                      <IconButton
                        aria-label="Copy source code"
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(
                          generateSourceBlock(module.source, versionToShow),
                          `Module source code for ${module.source} v${versionToShow}`
                        )}
                      >
                        <FaCopy />
                      </IconButton>
                    </HStack>
                    <Code 
                      display="block" 
                      whiteSpace="pre-wrap" 
                      p={2} 
                      fontSize="sm"
                      bg={codeBg}
                      color={textColor}
                      borderRadius="sm"
                      borderWidth="1px"
                      borderColor={cardBorder}
                    >
                      {generateSourceBlock(module.source, versionToShow)}
                    </Code>
                  </Box>

                  {/* README Viewer */}
                  <ReadmeViewer
                    namespace={module.source.split('/')[0]}
                    moduleName={module.source.split('/')[1]}
                    system={module.source.split('/')[2]}
                    version={versionToShow}
                  />
                </VStack>
              </Box>
            );
          })}
        </VStack>
      )}
    </VStack>
  );
}; 